import React, { useMemo } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ExercisesIcon } from 'assets/icons/general/exercises.svg';
import { ReactComponent as SheetsIcon } from 'assets/icons/general/sheets.svg';
import { ReactComponent as FlashcardsIcon } from 'assets/icons/general/flashcards.svg';
import { ReactComponent as MyProgressIcon } from 'assets/icons/general/my_progress.svg';
import SimpleStatistics from 'components/SimpleStatistics/SimpleStatistics';
import { FEATURE_FLAGS } from 'constants/dictionaries/featureFlags';
import PATHS from 'constants/paths';
import FullScreen from 'containers/FullScreen/FullScreen';
import useChosenExercisesStore from 'storages/chosenExercises/chosenExercises';
import useExercisesStore from 'storages/exercises/exercises';
import useSheetsStore from 'storages/sheets/sheets';
import useSubjectStore from 'storages/subject';
import SubjectSelectionTile from 'uniqueComponents/Dashboard/SubjectSelectionTile/SubjectSelectionTile';
import DashboardCard from 'uniqueComponents/Dashboard/DashboardCard/DashboardCard';
import { useQuery } from 'react-query';
import QUERY from 'constants/queryKeys';
import usersApi from 'api/rest/users/users';
import { useAuthContext } from 'context/AuthContext';
import { useFeatureFlag } from 'hooks/useFeatureFlags/useFeatureFlags';

const useStyles = makeStyles(theme => ({
  main: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    maxWidth: '950px',
    padding: theme.legacySpacing(0, 6),
    width: '100%',
    gridGap: '50px',
    alignItems: 'stretch',
    paddingTop: '2rem',

    '@media(max-width: 740px)': {
      gridTemplateColumns: '1fr',
      gridGap: '25px',
    },
  },
  rowCell: {
    display: 'grid',
    gridGap: '50px',
    '@media(max-width: 740px)': {
      gridGap: '25px',
    },
  },
  icon: {
    width: '100%',
    height: 'auto',
    display: 'block',
    '& path, rect, circle': {
      fill: theme.palette.primary.main,
    },
  },
  disabled: {
    '& path, rect, circle': {
      fill: theme.palette.text.disabled,
    },
  },
  chosenExercisesWrapper: {
    position: 'relative',
    width: '100%',
    background: 'blue',
    height: '100%',
  },
  chosenExercisesIcon: {
    background: theme.palette.background.default,
    position: 'absolute',
    zIndex: 1,
    transform: 'scale(0.9) translateY(20%)',
    transformOrigin: 'top left',
    top: 0,
    left: 0,
  },
  chosenExercisesIconShadow: {
    transform: 'scale(0.9) translateX(20%)',
    transformOrigin: 'top left',
    position: 'absolute',
    top: 0,
    left: 0,
  },
  chosenExercisesCounterDisabled: {
    background: theme.palette.grey[500],
  },
}));

const DashboardContent = () => {
  const navigate = useNavigate();
  const { subject } = useSubjectStore();
  const sheetsStore = useSheetsStore();
  const exercisesStore = useExercisesStore();
  const chosenExercisesStore = useChosenExercisesStore();
  const { isTeacher } = useAuthContext();
  const hasOnGoAccess = useFeatureFlag(FEATURE_FLAGS.ON_GO);

  const { data } = useQuery(QUERY.GET_ME, usersApi.getMe());
  const hasPremiumAccess = useMemo(() => data?.hasPremiumAccess, [data]);

  const getChosenExercisesCount = () => {
    if (!subject) return 0;
    if (!chosenExercisesStore?.chosenExercises[subject.subjectCode]) return 0;
    return chosenExercisesStore?.chosenExercises[subject.subjectCode].length;
  };

  const classes = useStyles({ chosenExercisesCount: getChosenExercisesCount() });
  if (!subject) {
    sheetsStore.reset();
    exercisesStore.reset();
    return navigate(PATHS.SUBJECT_SELECT);
  }
  return (
    <FullScreen>
      <div className={classes.main}>
        <SubjectSelectionTile />
        <SimpleStatistics />
        <div className={classes.rowCell}>
          <DashboardCard
            ariaLabel='Przeglądaj zadania'
            denseMobile
            description='Przeszukuj bazę zadań maturalnych według działów, filtrów i swoich oznaczeń.'
            heading='Przeglądaj zadania'
            layout='feature'
            to={PATHS.EXERCISES}
          >
            {/* TODO icon: open workbook / exercise list — a sheet of paper or notebook page with numbered lines or checkboxes */}
            <ExercisesIcon className={classes.icon} />
          </DashboardCard>
          <DashboardCard
            additionalText={!hasPremiumAccess ? 'Dostępne w Planie Premium' : null}
            ariaLabel='Rozwiązuj arkusze'
            denseMobile
            description='Pełne arkusze maturalne z poprzednich lat. Ćwicz jak na prawdziwej maturze.'
            disabled={!hasPremiumAccess}
            heading='Rozwiązuj arkusze'
            layout='feature'
            to={PATHS.SHEETS}
          >
            {/* TODO icon: exam paper with folded corner — a multi-page test sheet suggesting an official matura exam booklet */}
            <SheetsIcon className={clsx(classes.icon, !hasPremiumAccess && classes.disabled)} />
          </DashboardCard>
        </div>
        {isTeacher ? (
          <DashboardCard
            additionalText={!hasPremiumAccess && 'Dostępne w Planie Premium'}
            ariaLabel='Panel nauczyciela'
            denseMobile
            description='Przeglądaj postępy całej klasy i zadania oznaczone przez uczniów.'
            disabled={!hasPremiumAccess}
            heading='Panel nauczyciela'
            layout='feature'
            to={PATHS.TEACHER_DASHBOARD}
          >
            {/* TODO icon: classroom + people — chalkboard or desk with student silhouettes, optionally a teacher figure */}
            <MyProgressIcon className={clsx(classes.icon, !hasPremiumAccess && classes.disabled)} />
          </DashboardCard>
        ) : (
          <DashboardCard
            additionalText={!hasOnGoAccess ? 'Pracujemy nad tym' : null}
            ariaLabel='Rozwiązuj w głowie'
            denseMobile
            description='Szybkie powtórki w dowolnym momencie - w autobusie, w kolejce albo czekając na pizzę.'
            disabled={!hasOnGoAccess}
            heading='Rozwiązuj w głowie'
            layout='feature'
            to={PATHS.ON_GO}
          >
            {/* TODO icon: smartphone in hand — a hand holding a phone with a single question or checkmark on screen */}
            <FlashcardsIcon className={clsx(classes.icon, !hasOnGoAccess && classes.disabled)} />
          </DashboardCard>
        )}

        <DashboardCard
          additionalText={!hasPremiumAccess && 'Dostępne w Planie Premium'}
          ariaLabel='Twoje zestawy zadań'
          denseMobile
          description='Przeglądaj i twórz własne zestawy zadań.'
          disabled={!hasPremiumAccess}
          heading='Twoje zestawy zadań'
          layout='feature'
          to={PATHS.EXERCISES_SETS}
        >
          <div className={classes.chosenExercisesWrapper}>
            {/* TODO icon: stacked papers with a plus / pin — two offset sheets with a small "+" or pin badge */}
            <ExercisesIcon className={clsx(classes.icon, classes.chosenExercisesIcon, !hasPremiumAccess && classes.disabled)} />
            <ExercisesIcon className={clsx(classes.icon, classes.chosenExercisesIconShadow, !hasPremiumAccess && classes.disabled)} />
          </div>
        </DashboardCard>
      </div>
    </FullScreen>
  );
};

export default DashboardContent;
